/**
 * Tenant metadata browser / editor (Phase 8, P6B UI).
 *
 * Per-tenant view of the persisted table registry (TenantTableMetadata): list
 * rows, trigger discover / reconcile, review + approve staged (PENDING / STALE)
 * revisions, and hand-edit a row's descriptor + dictionary JSON. Gated
 * TENANT_METADATA_MANAGE. All mutations are re-authorized and re-embedded
 * server-side; the active tenant rides the request via the X-Tenant-Id header.
 */

import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, Alert, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import {
  listTenantMetadata, getTenantMetadata, updateTenantMetadata,
  discoverTenantMetadata, reconcileTenantMetadata, commitTenantMetadata,
} from '../../../utilityFunctions/PlatformAdminUtil';

const statusColor = (s) => {
  switch (s) {
    case 'ACTIVE': return 'success';
    case 'PENDING': return 'warning';
    case 'STALE': return 'warning';
    case 'RETIRED': return 'default';
    default: return 'default';
  }
};

// Human-readable labels for the persisted TenantTableMetadata.Source enum.
// Unknown / future sources fall back to the raw value so nothing ever breaks.
const SOURCE_LABELS = {
  CLASSPATH_SEED: 'Seed',
  CSV_INJECTION: 'CSV upload',
  DISCOVERED: 'Catalog',
  MIGRATED: 'Migrated',
  MANUAL: 'Manual',
  CRM_PUSH: 'CRM sync',
  FEDERATED_OBJECT_STORE: 'Federated (cloud storage)',
};
const sourceLabel = (s) => (s ? (SOURCE_LABELS[s] || s) : '—');

/**
 * Columns of a TableDefinition dictionary JSON (`{ name, description, columns: [...] }`),
 * or null when the JSON is absent / invalid / has no columns array — the review table is
 * then simply skipped and the raw JSON editor stays the source of truth.
 */
const parseDictionaryColumns = (json) => {
  if (!json || typeof json !== 'string' || !json.trim()) return null;
  try {
    const parsed = JSON.parse(json);
    const cols = parsed && Array.isArray(parsed.columns) ? parsed.columns : null;
    if (!cols) return null;
    return cols.filter((c) => c && typeof c === 'object');
  } catch (e) {
    return null;
  }
};

const cellText = (v) => {
  if (v == null || v === '') return '—';
  return typeof v === 'string' ? v : JSON.stringify(v);
};

// Read-only per-column review table for a dictionary (HITL review before approving).
function DictionaryColumnsTable({ columns, title }) {
  const { t } = useTranslation();
  const showSourceName = columns.some((c) => c.source_name != null && c.source_name !== '' && c.source_name !== c.name);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>{title}</Typography>
      <Box sx={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader sx={{ '& th': { fontWeight: 700, color: 'text.secondary' } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('TenantMetadata.columns.column')}</TableCell>
              {showSourceName && <TableCell>{t('TenantMetadata.columns.sourceName')}</TableCell>}
              <TableCell>{t('TenantMetadata.columns.type')}</TableCell>
              <TableCell>{t('TenantMetadata.columns.description')}</TableCell>
              <TableCell>{t('TenantMetadata.columns.redacted')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((c, i) => (
              <TableRow key={`${c.name || 'col'}-${i}`}>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{cellText(c.name)}</TableCell>
                {showSourceName && (
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {c.source_name != null && c.source_name !== c.name ? cellText(c.source_name) : '—'}
                  </TableCell>
                )}
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{cellText(c.type)}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>{cellText(c.description)}</TableCell>
                <TableCell>
                  {c.redacted === true
                    ? <Chip size="small" color="warning" label={t('TenantMetadata.columns.yes')} />
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

const hasPending = (r) =>
  r && (r.status === 'PENDING' || r.status === 'STALE' || r.pendingRevisedAt || r.pendingDescriptorJson);

export default function TenantMetadataScreen() {
  const { t } = useTranslation();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [editRow, setEditRow] = React.useState(null); // { id, tableName, descriptorJson, dictionaryJson }
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const res = await listTenantMetadata();
    setLoading(false);
    if (!res.ok) { setError(res.error); setRows([]); return; }
    setError(null);
    setRows(Array.isArray(res.data) ? res.data : []);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runAction = async (label, fn) => {
    setBusy(true);
    const res = await fn();
    setBusy(false);
    if (!res.ok) {
      swal({ title: `${label} failed`, text: res.error || 'Operation failed', icon: 'error' });
      return;
    }
    const summary = res.data && typeof res.data === 'object'
      ? JSON.stringify(res.data).slice(0, 300) : String(res.data || 'Done');
    swal({ title: `${label} complete`, text: summary, icon: 'success' });
    await load();
  };

  const openEditor = async (id) => {
    const res = await getTenantMetadata(id);
    if (!res.ok || !res.data) {
      swal({ title: 'Load failed', text: res.error || 'Could not load row', icon: 'error' });
      return;
    }
    const r = res.data;
    // GET {id} returns MetadataRowDetail: identity fields live under `summary`.
    const summary = r.summary || {};
    setEditRow({
      id: r.id ?? summary.id ?? id,
      tableName: r.tableName || summary.tableName || r.name || `#${id}`,
      descriptorJson: r.descriptorJson || '',
      dictionaryJson: r.dictionaryJson || '',
      pendingDictionaryJson: r.pendingDictionaryJson || '',
    });
  };

  // Read-only review tables, parsed from the (live-edited) dictionary JSON and any
  // staged revision. Invalid JSON → null → table skipped; the textareas stay as-is.
  const dictionaryColumns = React.useMemo(
    () => (editRow ? parseDictionaryColumns(editRow.dictionaryJson) : null),
    [editRow],
  );
  const pendingDictionaryColumns = React.useMemo(
    () => (editRow ? parseDictionaryColumns(editRow.pendingDictionaryJson) : null),
    [editRow],
  );

  const saveEdit = async () => {
    // Validate JSON client-side to save a round-trip; server re-validates.
    for (const [field, val] of [['Descriptor', editRow.descriptorJson], ['Dictionary', editRow.dictionaryJson]]) {
      if (val && val.trim()) {
        try { JSON.parse(val); } catch (e) {
          swal({ title: `${field} JSON is invalid`, text: e.message, icon: 'error' });
          return;
        }
      }
    }
    setSaving(true);
    const res = await updateTenantMetadata(editRow.id, editRow.descriptorJson, editRow.dictionaryJson);
    setSaving(false);
    if (!res.ok) {
      swal({ title: 'Save failed', text: res.error || 'Could not save', icon: 'error' });
      return;
    }
    setEditRow(null);
    await load();
  };

  const approve = async (id) => {
    const res = await commitTenantMetadata([id]);
    if (!res.ok) {
      swal({ title: 'Approve failed', text: res.error || 'Could not approve', icon: 'error' });
      return;
    }
    await load();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', mt: '5rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <StorageIcon sx={{ color: Colors.primary }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: Colors.primary }}>Tenant metadata</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Browse and curate the table registry the AI pipeline reads for this tenant. Discover new tables,
        reconcile drift, review staged revisions, and edit descriptors.
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="outlined" startIcon={<TravelExploreIcon />} disabled={busy}
          onClick={() => runAction('Discover', discoverTenantMetadata)}>Discover</Button>
        <Button variant="outlined" startIcon={<SyncIcon />} disabled={busy}
          onClick={() => runAction('Reconcile', reconcileTenantMetadata)}>Reconcile</Button>
        <Button variant="text" startIcon={<RefreshIcon />} disabled={loading} onClick={load}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {busy && <CustomLoader />}

      <Paper variant="outlined" sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 2 }}>
        {loading && <CustomLoader />}
        {!loading && rows.length === 0 && (
          <Alert severity="info">No metadata rows for this tenant yet. Use Discover to inspect the catalog.</Alert>
        )}
        {!loading && rows.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 820, '& th': { fontWeight: 700, color: 'text.secondary' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Table</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.tableName || r.name}</TableCell>
                    <TableCell>{r.tableKind || '—'}</TableCell>
                    <TableCell>{sourceLabel(r.source)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status || 'ACTIVE'} color={statusColor(r.status)} />
                    </TableCell>
                    <TableCell>{r.metadataVersion != null ? r.metadataVersion : '—'}</TableCell>
                    <TableCell align="right">
                      {hasPending(r) && (
                        <Tooltip title="Approve staged revision"><span>
                          <IconButton size="small" color="success" onClick={() => approve(r.id)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </span></Tooltip>
                      )}
                      <Tooltip title="Edit descriptor / dictionary"><span>
                        <IconButton size="small" onClick={() => openEditor(r.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="md" fullWidth>
        {editRow && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              Edit <span style={{ fontFamily: 'monospace' }}>{editRow.tableName}</span>
            </DialogTitle>
            <DialogContent dividers>
              {(dictionaryColumns?.length > 0 || pendingDictionaryColumns?.length > 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('TenantMetadata.columns.reviewHint')}
                </Typography>
              )}
              {pendingDictionaryColumns?.length > 0 && (
                <DictionaryColumnsTable
                  columns={pendingDictionaryColumns}
                  title={t('TenantMetadata.columns.pendingTitle', { n: pendingDictionaryColumns.length })}
                />
              )}
              {dictionaryColumns?.length > 0 && (
                <DictionaryColumnsTable
                  columns={dictionaryColumns}
                  title={t('TenantMetadata.columns.title', { n: dictionaryColumns.length })}
                />
              )}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Descriptor JSON</Typography>
              <TextField
                multiline minRows={6} fullWidth value={editRow.descriptorJson}
                onChange={(e) => setEditRow({ ...editRow, descriptorJson: e.target.value })}
                sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Dictionary JSON</Typography>
              <TextField
                multiline minRows={6} fullWidth value={editRow.dictionaryJson}
                onChange={(e) => setEditRow({ ...editRow, dictionaryJson: e.target.value })}
                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditRow(null)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={saveEdit} disabled={saving}
                sx={{ backgroundColor: Colors.primary }}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
